import { useMemo, useCallback, useEffect } from "react";
import { Passenger, SearchRequest } from "@services/travel";
import { UseFormSetValue, UseFormReturn, Control } from "react-hook-form";
import { DisabilityCard } from "@gen/travel/v1/common_pb";

interface UsePassengerCompanionLogicProps {
  control: Control<SearchRequest>;
  setValue: UseFormSetValue<SearchRequest>;
  mainPassengerForm: UseFormReturn<Passenger>;
  passengers: Passenger[];
  update: (index: number, passenger: Passenger) => void;
}

export const DEFAULT_COMPANION: DisabilityCard = DisabilityCard.UNSPECIFIED;
export const COMPANION_VALUE_UNSET: DisabilityCard = {} as DisabilityCard;

export function usePassengerCompanionLogic({
  mainPassengerForm,
  passengers,
  update,
}: UsePassengerCompanionLogicProps) {
  const beneficiaryIsDisabled = mainPassengerForm.watch("disability");
  const beneficiaryCompanionFare = mainPassengerForm.watch("hasCompanionFare");

  const helperMap = {
    beneficiary: {
      disabled: !!beneficiaryIsDisabled,
      hasCompanionFare: !!beneficiaryCompanionFare,
    },
    additionalPassenger1: {
      disabled: !!passengers?.[0]?.disability,
      hasCompanionFare: !!passengers?.[0]?.hasCompanionFare,
    },
    additionalPassenger2: {
      disabled: !!passengers?.[1]?.disability,
      hasCompanionFare: !!passengers?.[1]?.hasCompanionFare,
    },
  };

  // Automatically set additional passenger hasCompanionFare if main passenger is disabled
  useEffect(() => {
    if (beneficiaryIsDisabled) {
      const firstNonDisabledPassengerIndex =
        passengers?.findIndex(
          (passenger: Passenger) => !passenger.disability,
        ) ?? -1;

      const firstNonDisabledPassenger =
        passengers?.[firstNonDisabledPassengerIndex];

      if (
        firstNonDisabledPassenger &&
        (typeof firstNonDisabledPassenger.hasCompanionFare === "undefined" ||
          firstNonDisabledPassenger.hasCompanionFare === null)
      ) {
        update(firstNonDisabledPassengerIndex, {
          ...passengers[firstNonDisabledPassengerIndex],
          hasCompanionFare: DEFAULT_COMPANION,
        });
      }
    }
  }, [beneficiaryIsDisabled]);

  const numberOfCompanions = useMemo(() => {
    const passengers = [
      helperMap.beneficiary,
      helperMap.additionalPassenger1,
      helperMap.additionalPassenger2,
    ];

    return passengers.filter(
      (p) =>
        typeof p.hasCompanionFare !== "undefined" &&
        p.hasCompanionFare !== null,
    ).length;
  }, [helperMap]);

  const canBeACompanion = (
    passenger?: "beneficiary" | "additionalPassenger1" | "additionalPassenger2",
  ) => {
    const { additionalPassenger1, additionalPassenger2, beneficiary } =
      helperMap;
    if (passenger === "beneficiary") {
      return Boolean(
        additionalPassenger1.disabled || additionalPassenger2.disabled,
      );
    }
    if (passenger === "additionalPassenger1") {
      return Boolean(beneficiary.disabled || additionalPassenger2.disabled);
    }
    if (passenger === "additionalPassenger2") {
      return Boolean(beneficiary.disabled || additionalPassenger1.disabled);
    }

    return Boolean(
      additionalPassenger1.disabled ||
      additionalPassenger2.disabled ||
      beneficiary.disabled,
    );
  };

  // When we set a passenger as disabled, we automatically put another passenger as a companion, with priority other additional passenger > beneficiary
  const handleAdditionalPassengerFormSubmit = useCallback(
    ({
      passenger,
      otherPassengerIndex,
    }: {
      passenger: Passenger;
      otherPassengerIndex: number;
    }) => {
      if (!passenger.disability) {
        return;
      }

      if (
        beneficiaryCompanionFare !== null &&
        beneficiaryCompanionFare !== undefined
      ) {
        return;
      }

      const otherPassenger = passengers[otherPassengerIndex];
      if (otherPassenger && !otherPassenger.disability) {
        update(otherPassengerIndex, {
          ...otherPassenger,
          hasCompanionFare: DEFAULT_COMPANION,
        });
      } else if (!beneficiaryIsDisabled) {
        mainPassengerForm.setValue("hasCompanionFare", DEFAULT_COMPANION);
      }
    },
    [
      passengers,
      update,
      beneficiaryIsDisabled,
      beneficiaryCompanionFare,
      mainPassengerForm,
    ],
  );

  return {
    canBeACompanion,
    numberOfCompanions,
    handleAdditionalPassengerFormSubmit,
  };
}
